import { createApi } from '@reduxjs/toolkit/query/react'
import { type SkipToken } from '@reduxjs/toolkit/query'
import { BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { Team, User, Owner } from '../../models/User'
import {
  type AdditionalObjects,
  type CRMWorkflow,
  type ExportOptionsPayload,
  type IntegrationEntities,
  type IntegrationEntityResponseMap,
  type IntegrationEntityType,
  type WealthboxTag,
  type WorkflowTemplate,
} from '../../models/Integration'

export type { WealthboxTag }
import { type AccountType, type AccountOpeningTypeSelector } from '../../models/AccountOpening'
import { type AccountOpeningDescriptor } from '../../models/AccountOpeningDescriptor'
import { removeEmptyItems } from '../../common/util'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { IntegrationEmail, IntegrationEmailIdentity, IntegrationEmailThread } from '../../models/Email'
import { EmailFilterPayload } from '../../models/EmailFilter'
import { UserUpdateResponse } from '../../views/UpdateExternalContact/models'
import { SupportedObject } from '../../views/Setup/AIForms/model'
import { DocumentRecord } from '../../models/Document'
import { IntegrationEmailAttachment, EmailAttachmentMetadata } from '../../models/EmailAttachments'
import { aiAgentResultApi } from '../ai/aiAgentResult'
import { type RawWorkflowLists, withUpsertedWorkflow, type WorkflowListsCacheKey, workflowListsRequest } from './workflowLists'

// Define a service using a base URL and expected endpoints
const _BASE_URL = import.meta.env.REACT_APP_INTEGRATIONS_SERVICE_API_BASE_URL!

interface BaseConnectorRequest extends BaseRequest {
  connector: string
}

interface ConnectorEntityListRequest extends BaseConnectorRequest {
  entity: string
}

const connectorEntityList = <T>() => ({
  query: ({ accountId, connector, entity }: ConnectorEntityListRequest) => ({
    url: `/accounts/${accountId}/connectors/${connector}/get-connector-entities/${entity}`,
    method: 'POST',
    body: {},
  }),
  transformResponse: (response: BaseResponse<Array<Record<string, unknown>>>, _meta: unknown, arg: ConnectorEntityListRequest) => ({
    data: (response.data ?? []).flatMap((group) => (group[arg.entity] as T[] | undefined) ?? []),
  }),
})

export interface RelatedTo {
  id: string
  type?: string | null
}

export interface GetUsersByFilter extends BaseConnectorRequest {
  filters?: {
    name?: string
    type?: string
    externalIds?: string[]
    email?: string
  }
  includeExternalInfos?: boolean
  useExtendedSchema?: boolean
}

export interface UpdateUser extends BaseConnectorRequest {
  relatedTo: RelatedTo
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any
  contactChangeId?: string
  metadata?: Record<string, unknown>
}

export interface CreateContact extends BaseConnectorRequest {
  contact: { [key: string]: unknown }
  firstName?: string
  lastName?: string
  email?: string
  name?: string
  metadata?: Record<string, unknown>
  contactChangeId?: string
  internalContactId?: string
  ownerId?: string
}

export interface GetIntegrationEntitiesRequest extends BaseConnectorRequest {
  entityType: IntegrationEntityType
  suppressGlobalErrorToast?: boolean
}

export interface GetConnectorEntitiesRequest extends BaseConnectorRequest {
  entityType: string
  relatedTo?: string | null
}

export interface CreateIntegrationEntitiesRequest extends BaseConnectorRequest {
  entityType: string
  sourcePlatform?: string
  relatedTo?: RelatedTo | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: { [key: string]: any }
  entityChangeId?: string
}

export interface UpdateIntegrationEntitiesRequest extends BaseConnectorRequest {
  entityType: string
  entityId: string
  sourcePlatform?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: { [key: string]: any }
  relatedTo?: RelatedTo | null
  entityChangeId?: string
}

export interface StartIntegrationWorkflowRequest extends WorkflowListsCacheKey {
  payload: Record<string, unknown>
}

export interface CompleteIntegrationWorkflowStepRequest extends WorkflowListsCacheKey {
  entityId: string
  payload: Record<string, unknown>
}

export interface GetEntityRequest extends BaseConnectorRequest {
  objectName: string
  entityId: string
}

export interface GetEntitiesRequest extends BaseConnectorRequest {
  entityType: string
  contactExternalId?: string | null
  search?: string
  limit?: number
  cursor?: string | null
}

export interface EntityReferenceValue {
  label: string
  value?: string | null
}

export interface EntityListItem {
  id: string
  referenceValues: EntityReferenceValue[]
}

export interface EntityList {
  items: EntityListItem[]
  totalCount?: number | null
  loadedCount?: number | null
  nextCursor?: string | null
}

export interface GetAdditionalObjectsRequest extends BaseConnectorRequest {
  relatedTo: RelatedTo
  includeWorkflows?: boolean
  includeWorkflowTemplates?: boolean
  meetingStartDate?: string
  meetingEndDate?: string
}

export interface SaveMeetingRequest extends BaseConnectorRequest {
  body: SaveMeetingRequestPayload
}

export interface SaveMeetingRequestPayload {
  roomId: string
  sessionId: string
  description: string
  externalUserIds: string[]
  externalMeetingId?: string | null
  notifyUserId?: string | null
  notifyTeamId?: string | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  relatedTo: any
  exportType?: string | null
  aiAgentResultId?: string | null
  saveSyncStatus?: boolean
  exportOptions?: ExportOptionsPayload
}

export interface SaveNoteRequest extends BaseConnectorRequest {
  relatedTo: RelatedTo
  notifyUserId?: string | null
  notifyTeamId?: string | null
  roomId?: string
  sessionId?: string
  body: SaveNoteRequestPayload
}

export interface SaveNoteRequestPayload {
  note: string
}

export interface SaveResultRequest extends BaseConnectorRequest {
  userId: string
  body: SaveResultRequestPayload
}

export interface SaveResultToCRMRequest extends BaseConnectorRequest {
  body: SaveResultToCRMRequestPayload
}

export interface SaveResultToCRMRequestPayload {
  relatedTo: RelatedTo
  type: 'aiResult' | 'aiAgentResult'
  resultId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result?: { [key: string]: any }
  roomId?: string
  sessionId?: string
  notifyUserId?: string | null
  notifyTeamId?: string | null
  fileId?: string
}

export interface SaveAIAgentResultPayload {
  relatedTo: RelatedTo
  resultId: string
  roomId?: string
  sessionId?: string
  fileId?: string
  title?: string
}

export interface SaveAIAgentResultRequest extends BaseConnectorRequest {
  body: SaveAIAgentResultPayload
}

export interface SaveTasksRequest extends BaseConnectorRequest {
  body: SaveTasksRequestPayload
}

export interface SaveTasksRequestPayload {
  relatedTo: RelatedTo
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tasks: { [key: string]: any }[]
  saveSyncStatus?: boolean
}

export enum OperationDirection {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
}

export interface IntegrationOperationFilter {
  active?: boolean
  direction?: OperationDirection
  object?: string
  platform?: string
}

export interface RunContactSyncOperationContext {
  max_object_count: number
}

export type RunOperationContext = RunContactSyncOperationContext

export interface RunOperationRequest extends BaseConnectorRequest {
  context: RunOperationContext
  operation: IntegrationOperationFilter
}

export interface SaveResultRequestPayload {
  roomId: string
  sessionId: string
  aiFeatureId: string
  externalUserIds: string[]
  aiResultId?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extra: { [key: string]: any }
}

export interface IntegrationEmailFilterRequest {
  filter?: EmailFilterPayload
  filterId?: string
}

export interface FilterEmailThreadsRequest extends BaseRequest {
  emailConnector: string
  filters: IntegrationEmailFilterRequest
  expectedThreads?: number
  refresh?: boolean
}

export interface GetEmailThreadRequest extends BaseRequest {
  accountId: string
  emailConnector: string
  threadId: string
}

export interface GetEmailRequest extends BaseRequest {
  accountId: string
  emailConnector: string
  emailId: string
}

export interface CreateIntegrationEmailRequestBody {
  inReplyTo?: string
  recipients?: IntegrationEmailIdentity[]
  ccRecipients?: IntegrationEmailIdentity[]
  subject?: string
  body: string
  isDraft?: boolean
}

interface CreateIntegrationEmailRequest extends BaseConnectorRequest {
  body: CreateIntegrationEmailRequestBody
  suppressGlobalErrorToast?: boolean
}

export interface GetEmailThreadAttachmentsRequest extends BaseRequest {
  connector: string
  threadId: string
}

export interface LoadEmailAttachmentToDocumentRequest extends BaseRequest {
  connector: string
  emailExternalId: string
  attachmentExternalId: string
}

export interface GetEmailAttachmentsRequest extends BaseRequest {
  connector: string
  contactId: string
  createdAfter?: string
  page?: number
  size?: number
}

interface FilterIntegrationMappingTemplatesRequest {
  active?: boolean
  sourcePlatform?: string
  sourceObject?: string
  destinationPlatform?: string
  destinationObject?: string
}

interface IntegrationMappingTemplatePath {
  sourcePlatform: string
  sourceObject: string
  destinationPlatform: string
  destinationObject: string
  name: string
}

export interface GetDescribeContextDTO {
  objectName: string
}

export interface GetDescribeRequest extends BaseConnectorRequest {
  context: GetDescribeContextDTO
}

export interface GetContactSchemaContextDTO {
  objectName: string
  relatedTo?: RelatedTo
}

export interface GetContactSchemaRequest extends BaseConnectorRequest {
  context: GetContactSchemaContextDTO
}

export interface GetCrmEntitySchemaContextDTO {
  entityType: string
  relatedTo?: RelatedTo
}

export interface GetCrmEntitySchemaRequest extends BaseConnectorRequest {
  context: GetCrmEntitySchemaContextDTO
}

export interface WealthboxTags {
  tags: WealthboxTag[]
}

export interface SalesforceUserDefinedLabel {
  id: string
  name: string
}

export type IntegrationEntityRecord = Record<string, unknown>

const createEntityQuery = (request: CreateIntegrationEntitiesRequest) => ({
  url: `/accounts/${request.accountId}/connectors/${request.connector}/create-entity`,
  method: 'POST',
  body: {
    relatedTo: request.relatedTo,
    sourcePlatform: request.sourcePlatform ?? request.connector,
    sourceObject: request.entityType,
    entityType: request.entityType,
    entity: request.payload,
    entityChangeId: request.entityChangeId,
  },
})

const updateEntityQuery = (request: UpdateIntegrationEntitiesRequest) => ({
  url: `/accounts/${request.accountId}/connectors/${request.connector}/update-entity`,
  method: 'POST',
  body: {
    relatedTo: request.relatedTo,
    sourcePlatform: request.sourcePlatform ?? request.connector,
    sourceObject: request.entityType,
    entityType: request.entityType,
    entityId: request.entityId,
    entity: request.payload,
    entityChangeId: request.entityChangeId,
  },
})

export const integrationsApi = createApi({
  reducerPath: 'integrationsApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['EmailAttachment'],
  endpoints: (builder) => ({
    getIntegrationUsers: builder.query<BaseResponse<User[]>, GetUsersByFilter>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/get-contacts`,
          method: 'POST',
          body: {
            filters: request.filters,
            includeExternalInfos: request.includeExternalInfos,
            useExtendedSchema: request.useExtendedSchema,
          },
        }
      },
    }),
    getOwners: builder.query<BaseResponse<Owner[]>, BaseConnectorRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/get-connector-entities/owner`,
          method: 'POST',
          body: {},
        }
      },
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getContactSchema: builder.query<BaseResponse<{ [key: string]: any }>, GetContactSchemaRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/get-contact-schema`,
          method: 'POST',
          body: request.context,
        }
      },
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getCrmEntitySchema: builder.query<BaseResponse<{ [key: string]: any }>, GetCrmEntitySchemaRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/get-entity-schema`,
          method: 'POST',
          body: request.context,
        }
      },
    }),
    getIntegrationTeammates: builder.query<BaseResponse<User[]>, GetUsersByFilter>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/get-users`,
          method: 'POST',
          body: {
            filters: request.filters ? removeEmptyItems(request.filters) : {},
          },
        }
      },
    }),
    getIntegrationTeams: builder.query<BaseResponse<Team[]>, GetUsersByFilter>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/get-teams`,
          method: 'POST',
          body: {
            filters: request.filters ? removeEmptyItems(request.filters) : {},
          },
        }
      },
    }),
    getIntegrationEntities: builder.query<BaseResponse<IntegrationEntities>, GetIntegrationEntitiesRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/${request.entityType}`, // TODO: ?
        }
      },
    }),
    getConnectorEntities: builder.query<BaseResponse<IntegrationEntityRecord[]>, GetConnectorEntitiesRequest>({
      query: ({ accountId, connector, entityType, relatedTo = null }) => ({
        url: `/accounts/${accountId}/connectors/${connector}/get-connector-entities/${entityType}`,
        method: 'POST',
        body: { relatedTo },
      }),
    }),
    getAccountTypes: builder.query<BaseResponse<AccountType[]>, ConnectorEntityListRequest>(connectorEntityList<AccountType>()),
    getAccountTypeSelectors: builder.query<BaseResponse<AccountOpeningTypeSelector[]>, ConnectorEntityListRequest>(
      connectorEntityList<AccountOpeningTypeSelector>(),
    ),
    getEntitySchema: builder.query<BaseResponse<Record<string, unknown>>, BaseConnectorRequest & { entity: string; keyedBy: string; key: string }>({
      query: ({ accountId, connector, entity, keyedBy, key }) => ({
        url: `/accounts/${accountId}/connectors/${connector}/get-connector-entities/${entity}`,
        method: 'POST',
        body: { [keyedBy]: key },
      }),
      transformResponse: (response: BaseResponse<Array<Record<string, unknown>>>, _meta, arg) => ({
        data: (response.data?.[0]?.[arg.entity] as Record<string, unknown> | undefined) ?? {},
      }),
    }),
    getWorkflowTemplates: builder.query<BaseResponse<WorkflowTemplate[]>, BaseConnectorRequest>({
      query: ({ accountId, connector }) => ({
        url: `/accounts/${accountId}/connectors/${connector}/get-connector-entities/workflow_template`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: BaseResponse<Array<Record<string, unknown>>>) => ({
        data: (response.data ?? []).flatMap((group) => (group.workflow_template as WorkflowTemplate[] | undefined) ?? []),
      }),
    }),
    getPartyDetails: builder.query<BaseResponse<Record<string, unknown>>, BaseConnectorRequest & { entity: string; externalId: string; role: string }>({
      query: ({ accountId, connector, entity, externalId, role }) => ({
        url: `/accounts/${accountId}/connectors/${connector}/get-connector-entities/${entity}`,
        method: 'POST',
        body: { relatedTo: externalId, role },
      }),
      transformResponse: (response: BaseResponse<Array<Record<string, unknown>>>, _meta, arg) => ({
        data: (response.data?.[0]?.[arg.entity] as Record<string, unknown> | undefined) ?? {},
      }),
    }),
    getAccountOpeningDescriptor: builder.query<BaseResponse<AccountOpeningDescriptor>, BaseConnectorRequest>({
      query: ({ accountId, connector }) => ({
        url: `/accounts/${accountId}/connectors/${connector}/account-opening/descriptor`,
      }),
    }),
    runConnectorOperation: builder.mutation<
      BaseResponse<unknown>,
      { accountId: string; connector: string; operation: string; body: { payload: Record<string, unknown> } }
    >({
      query: ({ accountId, connector, operation, body }) => ({
        url: `/accounts/${accountId}/connectors/${connector}/${operation}`,
        method: 'POST',
        body,
      }),
    }),
    createIntegrationEntity: builder.mutation<BaseResponse<IntegrationEntityRecord>, CreateIntegrationEntitiesRequest>({
      query: createEntityQuery,
    }),
    updateIntegrationEntity: builder.mutation<BaseResponse<IntegrationEntityRecord>, UpdateIntegrationEntitiesRequest>({
      query: updateEntityQuery,
    }),
    startIntegrationWorkflow: builder.mutation<CRMWorkflow, StartIntegrationWorkflowRequest>({
      query: ({ accountId, connector, externalId, payload }) =>
        createEntityQuery({ accountId, connector, entityType: 'workflow', relatedTo: { id: externalId }, payload }),
      transformResponse: (response: BaseResponse<CRMWorkflow>) => response.data,
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const result = await queryFulfilled.catch(() => null)
        if (result) {
          dispatch(upsertWorkflowInLists(request, result.data))
        }
      },
    }),
    completeIntegrationWorkflowStep: builder.mutation<CRMWorkflow, CompleteIntegrationWorkflowStepRequest>({
      query: ({ accountId, connector, entityId, payload }) =>
        updateEntityQuery({ accountId, connector, entityType: 'workflow_steps', entityId, relatedTo: { id: '' }, payload }),
      transformResponse: (response: BaseResponse<CRMWorkflow>) => response.data,
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const result = await queryFulfilled.catch(() => null)
        if (result) {
          dispatch(upsertWorkflowInLists(request, result.data))
        }
      },
    }),
    getEntity: builder.query<Record<string, unknown>, GetEntityRequest>({
      query: ({ accountId, connector, objectName, entityId }) => ({
        url: `/accounts/${accountId}/connectors/${connector}/get-entity`,
        method: 'POST',
        body: {
          sourcePlatform: connector,
          sourceObject: objectName,
          entityId,
          destinationObject: objectName,
        },
      }),
      transformResponse: (response: BaseResponse<Record<string, unknown>>) => response.data,
    }),
    getEntities: builder.query<EntityList, GetEntitiesRequest>({
      query: ({ accountId, connector, entityType, contactExternalId, search, limit, cursor }) => ({
        url: `/accounts/${accountId}/connectors/${connector}/get-entities`,
        method: 'POST',
        body: {
          entityType,
          contactExternalId: contactExternalId || undefined,
          search: search || undefined,
          limit: limit ?? 25,
          cursor: cursor || undefined,
        },
      }),
      transformResponse: (response: BaseResponse<EntityList>) => response.data,
    }),
    getAdditionalObjects: builder.query<AdditionalObjects, GetAdditionalObjectsRequest>({
      query: (request) => {
        return {
          method: 'POST',
          url: `/accounts/${request.accountId}/connectors/${request.connector}/get-additional-objects`,
          body: {
            relatedTo: request.relatedTo,
            includeWorkflows: request.includeWorkflows,
            includeWorkflowTemplates: request.includeWorkflowTemplates,
            meetingStartDate: request.meetingStartDate,
            meetingEndDate: request.meetingEndDate,
          },
        }
      },
      transformResponse: (response: BaseResponse<AdditionalObjects>) => {
        return response.data
      },
    }),
    updateIntegrationUser: builder.mutation<UserUpdateResponse, UpdateUser>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/update-contact`,
          method: 'POST',
          body: {
            relatedTo: removeEmptyItems(request.relatedTo),
            source: request.connector,
            contact: removeEmptyItems(request.payload),
            contactChangeId: request.contactChangeId,
            metadata: request.metadata,
          },
        }
      },
    }),
    createIntegrationContact: builder.mutation<UserUpdateResponse, CreateContact>({
      query: ({ accountId, connector, contact, firstName, lastName, email, name, metadata, contactChangeId, internalContactId, ownerId }) => ({
        url: `/accounts/${accountId}/connectors/${connector}/create-contact`,
        method: 'POST',
        body: {
          source: connector,
          contact: removeEmptyItems(contact),
          firstName,
          lastName,
          email,
          name,
          metadata,
          contactChangeId,
          internalContactId,
          ownerId,
        },
      }),
    }),
    saveMeeting: builder.mutation<void, SaveMeetingRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/save-meeting`,
          body: request.body,
          method: 'POST',
        }
      },
    }),
    saveNote: builder.mutation<void, SaveNoteRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/save-note`,
          body: {
            roomId: request.roomId,
            sessionId: request.sessionId,
            relatedTo: request.relatedTo,
            notifyUserId: request.notifyUserId,
            notifyTeamId: request.notifyTeamId,
            note: request.body.note,
          },
          method: 'POST',
        }
      },
    }),
    saveResult: builder.mutation<void, SaveResultRequest>({
      // @deprecated This using the old crm layer should use the other
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/users/${request.userId}/create-result`,
          body: request.body,
          method: 'POST',
        }
      },
    }),
    saveResultToCRM: builder.mutation<void, SaveResultToCRMRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/save-result`,
          body: request.body,
          method: 'POST',
        }
      },
    }),
    saveAIAgentResult: builder.mutation<void, SaveAIAgentResultRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/save-ai-agent-result`,
          body: request.body,
          method: 'POST',
        }
      },
      async onQueryStarted(_request, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(aiAgentResultApi.util.invalidateTags(['AgentResult']))
      },
    }),
    saveTasks: builder.mutation<void, SaveTasksRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/save-tasks`,
          body: request.body,
          method: 'POST',
        }
      },
    }),
    getEmailThread: builder.query<IntegrationEmailThread, GetEmailThreadRequest>({
      query: (request) => {
        const { accountId, emailConnector, threadId } = request
        return {
          url: `/accounts/${accountId}/email-connectors/${emailConnector}/emails/threads/${threadId}`,
        }
      },
      transformResponse: (response: BaseResponse<IntegrationEmailThread>) => {
        return response.data
      },
    }),
    getEmail: builder.query<IntegrationEmail, GetEmailRequest>({
      query: (request) => {
        const { accountId, emailConnector, emailId } = request
        return {
          url: `/accounts/${accountId}/email-connectors/${emailConnector}/emails/${emailId}`,
          validateStatus: (response) => (response.status >= 200 && response.status <= 299) || response.status === 404,
        }
      },
      transformResponse: (response: BaseResponse<IntegrationEmail>) => {
        return response.data
      },
    }),
    filterEmailThreads: builder.mutation<BasePageResponse<IntegrationEmailThread[]>, FilterEmailThreadsRequest>({
      query: (request) => {
        const { accountId, emailConnector, expectedThreads, refresh, filters } = request
        return {
          method: 'POST',
          url: `/accounts/${accountId}/email-connectors/${emailConnector}/emails/threads/filter`,
          params: {
            expectedThreads,
            refresh,
          },
          body: filters,
        }
      },
    }),
    createIntegrationEmail: builder.mutation<IntegrationEmail, CreateIntegrationEmailRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/email-connectors/${request.connector}/emails`,
          body: request.body,
          method: 'POST',
        }
      },
    }),
    getEmailThreadAttachments: builder.query<IntegrationEmailAttachment[], GetEmailThreadAttachmentsRequest>({
      query: (request) => {
        const { accountId, connector, threadId } = request
        return {
          url: `/accounts/${accountId}/email-connectors/${connector}/emails/threads/${threadId}/attachments`,
          method: 'GET',
        }
      },
      transformResponse: (response: BaseResponse<IntegrationEmailAttachment[]>) => {
        return response.data
      },
    }),
    loadEmailAttachmentToDocument: builder.mutation<DocumentRecord, LoadEmailAttachmentToDocumentRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/email-connectors/${request.connector}/email-attachments/load-to-document`,
          method: 'POST',
          body: { emailExternalId: request.emailExternalId, attachmentExternalId: request.attachmentExternalId },
        }
      },
      transformResponse: (response: BaseResponse<DocumentRecord>) => {
        return response.data
      },
      invalidatesTags: [{ type: 'EmailAttachment', id: 'LIST' }],
      async onQueryStarted(_request, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
        } catch {
          dispatch(integrationsApi.util.invalidateTags([{ type: 'EmailAttachment', id: 'LIST' }]))
        }
      },
    }),
    getEmailAttachments: builder.query<BasePageResponse<EmailAttachmentMetadata[]>, GetEmailAttachmentsRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/email-connectors/${request.connector}/email-attachments/metadata`,
        method: 'GET',
        params: {
          contactId: request.contactId,
          ...(request.createdAfter ? { createdAfter: request.createdAfter } : {}),
          page: request.page ?? 0,
          size: request.size ?? 200,
        },
      }),
      providesTags: [{ type: 'EmailAttachment', id: 'LIST' }],
    }),
    filterIntegrationMappingTemplates: builder.query<IntegrationMappingTemplatePath[], FilterIntegrationMappingTemplatesRequest>({
      query: (request) => ({
        url: '/integration-mapping-templates',
        method: 'GET',
        params: request,
      }),
      transformResponse: (response: BaseResponse<IntegrationMappingTemplatePath[]>) => response.data,
    }),
    runOperation: builder.mutation<BaseResponse<BaseResponse<Record<string, unknown>>>, RunOperationRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/connectors/${request.connector}/run-integration-operation`,
        method: 'POST',
        body: {
          context: { context: request.context },
          operation: request.operation,
        },
      }),
    }),
    getSupportedObjects: builder.query<SupportedObject[], BaseConnectorRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/connectors/${request.connector}/get-supported-objects`,
        method: 'GET',
      }),
      transformResponse: (
        response: BaseResponse<{
          supported_object: SupportedObject[]
        }>,
      ) => response.data.supported_object,
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getDescribe: builder.query<BaseResponse<any>, GetDescribeRequest>({
      query: ({ accountId, connector, context }) => ({
        url: `/accounts/${accountId}/connectors/${connector}/get-describe`,
        method: 'POST',
        body: context,
      }),
    }),
  }),
})

const upsertWorkflowInLists = (cacheKey: WorkflowListsCacheKey, workflow: CRMWorkflow) =>
  integrationsApi.util.updateQueryData('getAdditionalObjects', workflowListsRequest(cacheKey), (draft) => {
    Object.assign(draft, withUpsertedWorkflow(draft as RawWorkflowLists<CRMWorkflow>, workflow))
  })

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetIntegrationUsersQuery,
  useGetOwnersQuery,
  useGetIntegrationEntitiesQuery: useUntypedGetIntegrationEntitiesQuery,
  useLazyGetIntegrationEntitiesQuery: useUntypedLazyGetIntegrationEntitiesQuery,
  useGetConnectorEntitiesQuery,
  useGetAccountTypesQuery,
  useGetAccountTypeSelectorsQuery,
  useLazyGetEntitySchemaQuery,
  useLazyGetPartyDetailsQuery,
  useGetAccountOpeningDescriptorQuery,
  useGetWorkflowTemplatesQuery,
  useRunConnectorOperationMutation,
  useLazyGetIntegrationUsersQuery,
  useUpdateIntegrationUserMutation,
  useCreateIntegrationContactMutation,
  useSaveMeetingMutation,
  useSaveResultMutation,
  useSaveNoteMutation,
  useGetAdditionalObjectsQuery,
  useLazyGetAdditionalObjectsQuery,
  useCreateIntegrationEntityMutation,
  useUpdateIntegrationEntityMutation,
  useStartIntegrationWorkflowMutation,
  useCompleteIntegrationWorkflowStepMutation,
  useLoadEmailAttachmentToDocumentMutation,
  useGetEmailAttachmentsQuery,
  useGetEmailQuery,
  useLazyGetEmailThreadAttachmentsQuery,
  useGetIntegrationTeammatesQuery,
  useLazyGetIntegrationTeammatesQuery,
  useGetIntegrationTeamsQuery,
  useLazyGetIntegrationTeamsQuery,
  useFilterEmailThreadsMutation,
  useLazyGetEmailThreadQuery,
  useCreateIntegrationEmailMutation,
  useSaveResultToCRMMutation,
  useSaveTasksMutation,
  useLazyGetEmailQuery,
  useFilterIntegrationMappingTemplatesQuery,
  useGetSupportedObjectsQuery,
  useGetDescribeQuery,
  useLazyGetDescribeQuery,
  useGetContactSchemaQuery,
  useLazyGetContactSchemaQuery,
  useGetCrmEntitySchemaQuery,
  useLazyGetCrmEntitySchemaQuery,
  useSaveAIAgentResultMutation,
  useRunOperationMutation,
  useGetEntityQuery,
  useLazyGetEntityQuery,
  useGetEntitiesQuery,
  useLazyGetEntitiesQuery,
} = integrationsApi

type IntegrationEntitiesArg<T extends IntegrationEntityType> = Omit<GetIntegrationEntitiesRequest, 'entityType'> & { entityType: T }

// builder.query fixes the result type when the endpoint is declared, so the generated
// hooks cannot let the entity type in the request select the response shape. These
// signatures apply that link by hand and therefore describe only the parts of the hooks
// the callers use; reach for the untyped hooks for anything else.
export const useGetIntegrationEntitiesQuery = useUntypedGetIntegrationEntitiesQuery as unknown as <T extends IntegrationEntityType>(
  arg: IntegrationEntitiesArg<T> | SkipToken,
  options?: { skip?: boolean },
) => { data?: BaseResponse<IntegrationEntityResponseMap[T]>; isFetching: boolean; isLoading: boolean; isError: boolean }

export const useLazyGetIntegrationEntitiesQuery = useUntypedLazyGetIntegrationEntitiesQuery as unknown as () => [
  <T extends IntegrationEntityType>(arg: IntegrationEntitiesArg<T>) => { unwrap: () => Promise<BaseResponse<IntegrationEntityResponseMap[T]>> },
]
